import { z } from 'zod'
import type { DayId, DayKind } from './calendar.ts'
import { isDayId } from './calendar.ts'
import {
  BadgeIdSchema,
  CommandIdSchema,
  DeviceIdSchema,
  EventIdSchema,
  FamilyIdSchema,
  MemberIdSchema,
  PhotoIdSchema,
  PurchaseIdSchema,
  RewardIdSchema,
  StepIdSchema,
  TaskIdSchema,
  type DeviceId,
  type EventId,
  type MemberId,
} from './ids.ts'

/**
 * The event vocabulary. This file is the load-bearing one.
 *
 * The central rule of the house — points are never taken away as a consequence —
 * is enforced here as a property of the language rather than as a rule someone
 * has to remember: there is no `CoinsDeducted`, no `XpRemoved`, no
 * `StreakPenalised`, and *nothing* lowers experience.
 *
 * Exactly two events lower a coin balance. `PurchaseMade` is the child choosing
 * to spend. `ValidationRevoked` is a parent undoing their own mistake — they
 * confirmed the wrong task — and it is deliberately narrow: it needs a written
 * reason, it claws back at most what that one validation gave, and it can never
 * push a balance below zero. It is a correction, never a punishment.
 *
 * Events are append-only and immutable. Everything else in the app — today's
 * screen, the wallet, the ladder, the weekly meeting summary — is a projection
 * derived by replaying them.
 */

// --- Shared value schemas ----------------------------------------------------

export const DayIdSchema = z
  .string()
  .refine(isDayId, 'Not a valid day id') as unknown as z.ZodType<DayId>

/** ISO-8601 instant with an explicit offset, e.g. `2026-09-13T18:02:11+02:00`. */
export const InstantSchema = z.iso.datetime({ offset: true })

export const DayKindSchema = z.enum([
  'weekday',
  'weekend',
  'holiday',
  'vacation',
  'special',
  'sick',
  'paused',
]) as unknown as z.ZodType<DayKind>

/**
 * Los juegos que hay. De momento uno; existe como enumeración y no como texto
 * libre para que añadir el segundo sea un cambio que el compilador acompañe.
 */
export const GameIdSchema = z.enum(['mates'])
export type GameId = z.infer<typeof GameIdSchema>

export const OpSchema = z.enum(['x', ':'])
export type Op = z.infer<typeof OpSchema>

/**
 * Una pregunta de una sesión de repaso, con lo que contestó y cuánto tardó.
 *
 * El tiempo se guarda porque es la mitad de la información: un acierto lento es
 * contar con los dedos, y sin ese dato el juego no sabría en qué insistir. Y se
 * guarda aquí, en el registro, y no en un contador aparte, por lo de siempre —
 * lo que no está en el registro no sobrevive a una tablet rota.
 *
 * Hay dos formas, y las dos se aceptan. La de arriba es la buena: la destreza
 * —«restar con decimales»— más el enunciado y lo que contestó, en texto, que es
 * lo único que sirve cuando hay decimales de por medio. La de abajo es la de los
 * primeros días, cuando el juego solo hacía tablas y todo cabía en dos enteros.
 *
 * Los campos viejos siguen aquí, opcionales, **por los archivos ya exportados**:
 * si esta forma dejara de validar, importar una copia de seguridad anterior
 * rechazaría esas sesiones en silencio. Un formato que no se puede leer hacia
 * atrás convierte una copia de seguridad en un archivo bonito y nada más.
 */
export const PracticeItemSchema = z.object({
  destreza: z.string().trim().min(1).max(40).optional(),
  /** Tal y como se le enseñó: «34,5 + 12,7». */
  pide: z.string().trim().min(1).max(60).optional(),
  /** Lo que escribió, en texto: «47,2». */
  dijo: z.string().trim().max(20).optional(),

  /** Forma antigua: en una división, `a` era el producto (56 : 7). */
  a: z.int().min(1).max(100).optional(),
  b: z.int().min(1).max(100).optional(),
  op: OpSchema.optional(),
  said: z.int().min(0).max(1000).optional(),

  ok: z.boolean(),
  /** Acotado por arriba: si deja la tablet en la mesa, eso no es una respuesta. */
  ms: z.int().min(0).max(600_000),
  /** Lo hizo paso a paso, con la cuenta delante. Opcional: lo de antes no lo tenía. */
  ayuda: z.boolean().optional(),
})
export type PracticeItem = z.infer<typeof PracticeItemSchema>

export const TaskKindSchema = z.enum(['routine', 'mission'])
export type TaskKind = z.infer<typeof TaskKindSchema>

export const MomentSchema = z.enum(['morning', 'afternoon', 'evening'])
export type Moment = z.infer<typeof MomentSchema>

/** The four rungs of the autonomy ladder. What changes is who reminds, who
 *  validates and what is earned — not how hard the task is. */
export const RungSchema = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)])
export type Rung = z.infer<typeof RungSchema>

export const StepSchema = z.object({
  id: StepIdSchema,
  text: z.string().trim().min(1).max(120),
  emoji: z.string().max(8).optional(),
})
export type Step = z.infer<typeof StepSchema>

/** Coins and XP per rung. Spelled out, because "half of 5 coins" is 2.5. */
export const RewardLadderSchema = z.object({
  1: z.object({ coins: z.int().min(0), xp: z.int().min(0) }),
  2: z.object({ coins: z.int().min(0), xp: z.int().min(0) }),
  3: z.object({ coins: z.int().min(0), xp: z.int().min(0) }),
  4: z.object({ coins: z.int().min(0), xp: z.int().min(0) }),
})
export type RewardLadder = z.infer<typeof RewardLadderSchema>

export const ScreenLimitsSchema = z.object({
  /** Minutes the chest opens with, by budget kind. */
  base: z.object({ weekday: z.int().min(0).max(480), weekend: z.int().min(0).max(480) }),
  /** Ceiling on minutes that can be bought on top, by budget kind. */
  extraCap: z.object({ weekday: z.int().min(0).max(480), weekend: z.int().min(0).max(480) }),
  /** Family Link grants time in blocks; the shop sells the same size. */
  blockMinutes: z.int().min(1).max(60),
})
export type ScreenLimits = z.infer<typeof ScreenLimitsSchema>

export const AdaptationsSchema = z.object({
  /** No irony, no "almost", no exclamations. Checked by a test over the strings. */
  literalLanguage: z.boolean(),
  /** Surprise chests stay hidden; everything shows its contents up front. */
  noSurprises: z.boolean(),
  reducedMotion: z.boolean(),
  largeText: z.boolean(),
  readAloud: z.boolean(),
  /** Days of notice before a ladder promotion takes effect. */
  promotionNoticeDays: z.int().min(0).max(14),
  /**
   * One task on the screen at a time, and nothing else.
   *
   * A day with twelve cards on it is twelve decisions before the first move.
   * For a child who stalls at the start, the screen showing exactly one thing
   * is often the whole difference. Optional so that events already written
   * before this existed still read.
   */
  oneThingAtATime: z.boolean().optional(),
})
export type Adaptations = z.infer<typeof AdaptationsSchema>

export const ActorSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('child'), id: MemberIdSchema }),
  z.object({ kind: z.literal('parent'), id: MemberIdSchema }),
  z.object({ kind: z.literal('system') }),
])
export type Actor = z.infer<typeof ActorSchema>

// --- Event definitions -------------------------------------------------------

const event = <T extends string, V extends number, P extends z.ZodType>(
  type: T,
  v: V,
  payload: P,
) =>
  z.object({
    type: z.literal(type),
    v: z.literal(v),
    at: InstantSchema,
    day: DayIdSchema,
    actor: ActorSchema,
    payload,
  })

const reason = z.string().trim().max(280)

export const EVENT_SCHEMAS = [
  // -- Configuration ---------------------------------------------------------
  event(
    'FamilyConfigured',
    1,
    z.object({
      familyId: FamilyIdSchema,
      timezone: z.string().min(1),
      dayCutoffHour: z.int().min(0).max(23),
      /** ISO weekday of the weekly meeting. 7 = Sunday. */
      meetingDayOfWeek: z.int().min(1).max(7),
    }),
  ),
  event(
    'MemberAdded',
    1,
    z.object({
      memberId: MemberIdSchema,
      name: z.string().trim().min(1).max(40),
      role: z.enum(['child', 'parent']),
      birthYear: z.int().min(1900).max(2200).optional(),
      accent: z.string().regex(/^#[0-9a-f]{6}$/i),
      emoji: z.string().max(8).optional(),
      adaptations: AdaptationsSchema,
      limits: ScreenLimitsSchema,
      /** Content, not mechanics: unlocks the gluten-free autonomy missions. */
      glutenFree: z.boolean(),
    }),
  ),
  event(
    'TaskDefined',
    1,
    z.object({
      taskId: TaskIdSchema,
      memberId: MemberIdSchema,
      kind: TaskKindSchema,
      moment: MomentSchema.optional(),
      name: z.string().trim().min(1).max(60),
      emoji: z.string().max(8).optional(),
      steps: z.array(StepSchema).max(8),
      difficulty: z.int().min(1).max(3),
      /** Routines carry zero coins on every rung: they are contribution, not work. */
      rewards: RewardLadderSchema,
      promotionThreshold: z.int().min(1).max(60),
      requiresPhoto: z.boolean(),
      /** Where on the ladder this task starts. Most begin at 1, but a task the
       *  child already does unprompted can be placed higher from the start. */
      initialRung: RungSchema.optional(),
      /**
       * Qué juego la da por hecha. Una misión con juego se valida sola cuando el
       * niño termina la sesión: nadie tiene que comprobar unas tablas, y el niño
       * no espera a que un adulto pase por ahí.
       */
      game: GameIdSchema.optional(),
    }),
  ),
  event(
    'MemberAdaptationsUpdated',
    1,
    z.object({ memberId: MemberIdSchema, adaptations: AdaptationsSchema }),
  ),
  /**
   * Un miembro sale de las listas. No borra: su historia sigue en el registro,
   * porque pasó. Lo que hace falta es poder quitar de en medio a un duplicado que
   * entró al importar una copia, o a alguien que ya no vive en la casa.
   */
  event(
    'MemberArchived',
    1,
    z.object({ memberId: MemberIdSchema, reason: z.string().trim().max(120) }),
  ),
  event('MemberRestored', 1, z.object({ memberId: MemberIdSchema })),
  /**
   * Para quién vale un premio.
   *
   * Hace falta porque los premios se definen con la lista de hijos que había ese
   * día: un hijo dado de alta después se quedaba con la tienda vacía, y no existía
   * ninguna manera de arreglarlo salvo volver a crear el catálogo entero.
   */
  event(
    'RewardMembersChanged',
    1,
    z.object({ rewardId: RewardIdSchema, memberIds: z.array(MemberIdSchema).min(1) }),
  ),
  /** The wording changed. A clearer name is a clearer agreement, so this takes
   *  effect at once rather than waiting for the weekly meeting. */
  event(
    'TaskRenamed',
    1,
    z.object({
      taskId: TaskIdSchema,
      name: z.string().trim().min(1).max(60),
      emoji: z.string().max(8).optional(),
    }),
  ),
  event('TaskRestored', 1, z.object({ taskId: TaskIdSchema })),
  event(
    'TaskStepsUpdated',
    1,
    z.object({ taskId: TaskIdSchema, steps: z.array(StepSchema).max(8) }),
  ),
  /**
   * Lo que paga una tarea, cambiado después de haberla creado.
   *
   * Existe porque no estaba: se podía poner el precio al crear la tarea y
   * después no había forma de tocarlo, que es justo lo contrario de para lo que
   * existe una reunión semanal. La regla de que una rutina no paga monedas se
   * comprueba aquí igual que al crearla.
   */
  event('TaskRewardsChanged', 1, z.object({ taskId: TaskIdSchema, rewards: RewardLadderSchema })),
  event('TaskReferencePhotoSet', 1, z.object({ taskId: TaskIdSchema, photoId: PhotoIdSchema })),
  event('TaskArchived', 1, z.object({ taskId: TaskIdSchema, reason })),
  /**
   * El aspecto de un niño en su propia app: su dibujo, su color y cómo se
   * llaman sus monedas.
   *
   * No es decoración. En TEA, meter el interés fuerte del niño dentro del juego
   * es el motivador más potente que existe, y esto es la forma de hacerlo sin
   * traer contenido de nadie: los nombres y los dibujos los pone la familia.
   */
  /**
   * A qué hora se le recuerda a un niño lo que toca.
   *
   * Vive en el registro y no en el servidor porque de aquí sale el aviso que se
   * ve **en la pantalla**, que es el que de verdad hace que alguien empiece: una
   * notificación es un instante y esto es un estado. El servidor recibe una
   * copia de estas horas para mandar además su golpe en la puerta, y si no la
   * recibe nunca, la app sigue avisando igual.
   */
  event(
    'RemindersSet',
    1,
    z.object({
      memberId: MemberIdSchema,
      avisos: z
        .array(
          z.object({
            hora: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
            previo: z.boolean(),
          }),
        )
        .max(6),
    }),
  ),
  event(
    'MemberLookChanged',
    1,
    z.object({
      memberId: MemberIdSchema,
      accent: z.string().regex(/^#[0-9a-f]{6}$/i),
      emoji: z.string().max(8).optional(),
      /** Cómo llama este niño a sus monedas. Vacío es «monedas». */
      coinWord: z.string().trim().max(20).optional(),
    }),
  ),
  event(
    'RewardDefined',
    1,
    z.object({
      rewardId: RewardIdSchema,
      name: z.string().trim().min(1).max(60),
      price: z.int().min(0),
      kind: z.enum(['screen', 'privilege', 'family', 'social', 'big']),
      memberIds: z.array(MemberIdSchema).min(1),
      notes: z.string().trim().max(140).optional(),
      /** One mechanism for the daily screen cap and for "one skip per week". */
      limit: z.object({ per: z.enum(['day', 'week']), max: z.int().min(1) }).optional(),
    }),
  ),
  /**
   * Out of the shop, not out of the history. Same reasoning as an archived task:
   * what was bought last month was bought, and the log has to keep saying so.
   */
  event('RewardArchived', 1, z.object({ rewardId: RewardIdSchema, reason })),
  /** Prices only change at the weekly meeting, so a change is always scheduled. */
  event(
    'PriceChangeScheduled',
    1,
    z.object({
      rewardId: RewardIdSchema,
      newPrice: z.int().min(0),
      effectiveFrom: DayIdSchema,
    }),
  ),
  event(
    'LimitChangeScheduled',
    1,
    z.object({
      memberId: MemberIdSchema,
      limits: ScreenLimitsSchema,
      effectiveFrom: DayIdSchema,
    }),
  ),
  event(
    'DayTypeDeclared',
    1,
    z.object({ day: DayIdSchema, kind: DayKindSchema, note: reason.optional() }),
  ),
  /**
   * Un tramo entero del calendario de una vez.
   *
   * Existe porque lo que un adulto decide no son diecinueve días sueltos: son
   * «las vacaciones de Navidad». Guardar el tramo y no los días es guardar lo
   * que de verdad se acordó, y además es lo que permite volver a leerlo en la
   * reunión sin reconstruir nada.
   */
  event(
    'DayRangeDeclared',
    1,
    z.object({
      from: DayIdSchema,
      to: DayIdSchema,
      kind: DayKindSchema,
      note: z.string().trim().max(80).optional(),
    }),
  ),
  event('DayPaused', 1, z.object({ day: DayIdSchema, memberId: MemberIdSchema, reason })),
  /** Announced in advance and shown on the "change of plan" screen. */
  event(
    'PlanChangeAnnounced',
    1,
    z.object({
      text: z.string().trim().min(1).max(280),
      from: DayIdSchema,
      until: DayIdSchema.optional(),
    }),
  ),

  // -- Doing the tasks -------------------------------------------------------
  event(
    'StepCompleted',
    1,
    z.object({ taskId: TaskIdSchema, memberId: MemberIdSchema, stepId: StepIdSchema }),
  ),
  /** A mis-tap, undone. Nothing is taken away, because a step that is not
   *  validated has not paid anything yet. */
  event(
    'StepUncompleted',
    1,
    z.object({ taskId: TaskIdSchema, memberId: MemberIdSchema, stepId: StepIdSchema }),
  ),
  event(
    'TaskMarkedDone',
    1,
    z.object({
      taskId: TaskIdSchema,
      memberId: MemberIdSchema,
      commandId: CommandIdSchema,
      photoId: PhotoIdSchema.optional(),
      /** True on rung 3 and above: the child validated it themselves. */
      selfValidated: z.boolean(),
    }),
  ),
  event(
    'TaskValidated',
    1,
    z.object({
      taskId: TaskIdSchema,
      memberId: MemberIdSchema,
      commandId: CommandIdSchema,
      rung: RungSchema,
      coins: z.int().min(0),
      xp: z.int().min(0),
    }),
  ),
  /** "A step is missing." Nothing is taken away; the task goes back to pending. */
  event(
    'TaskReturned',
    1,
    z.object({
      taskId: TaskIdSchema,
      memberId: MemberIdSchema,
      missingStepId: StepIdSchema.optional(),
      note: reason.optional(),
    }),
  ),
  event(
    'ValidationRevoked',
    1,
    z.object({
      taskId: TaskIdSchema,
      memberId: MemberIdSchema,
      revokedEventId: EventIdSchema,
      reason,
    }),
  ),
  /** The 1-in-4 spot check. The draw is recorded, never recomputed on replay. */
  event(
    'CheckRequested',
    1,
    z.object({
      taskId: TaskIdSchema,
      memberId: MemberIdSchema,
      drawn: z.number().min(0).max(1),
    }),
  ),
  event(
    'CheckResolved',
    1,
    z.object({
      taskId: TaskIdSchema,
      memberId: MemberIdSchema,
      outcome: z.enum(['ok', 'pending']),
    }),
  ),

  // -- Screen time -----------------------------------------------------------
  event('ChestOpened', 1, z.object({ memberId: MemberIdSchema, minutes: z.int().min(0) })),
  event(
    'PurchaseMade',
    1,
    z.object({
      purchaseId: PurchaseIdSchema,
      memberId: MemberIdSchema,
      rewardId: RewardIdSchema,
      commandId: CommandIdSchema,
      /** Price at the moment of purchase; prices change, history does not. */
      price: z.int().min(0),
      quantity: z.int().min(1).max(20),
    }),
  ),
  /**
   * The child changed their mind before an adult handed anything over.
   *
   * It is deliberately narrow: only while the purchase is still waiting. Once a
   * parent has granted it, the minutes exist and the coins are spent. That is
   * the same line real life draws — you can put it back on the shelf before you
   * pay, not after you have eaten it — and it is what keeps a price meaning
   * something while still letting a child press a button without fear.
   */
  event(
    'PurchaseCancelled',
    1,
    z.object({ purchaseId: PurchaseIdSchema, memberId: MemberIdSchema, refund: z.int().min(0) }),
  ),
  /** A parent actually granted the minutes in Family Link. Without this the app's
   *  balance and the tablet's reality drift apart within a week. */
  event('PurchaseGranted', 1, z.object({ purchaseId: PurchaseIdSchema, minutes: z.int().min(0) })),
  event('PurchaseDelivered', 1, z.object({ purchaseId: PurchaseIdSchema })),
  event(
    'TimerStarted',
    1,
    z.object({
      memberId: MemberIdSchema,
      minutes: z.int().min(1),
      endsAt: InstantSchema,
      source: z.enum(['chest', 'purchase']),
    }),
  ),

  // -- Streak, ladder, recognition -------------------------------------------
  event('StreakAdvanced', 1, z.object({ memberId: MemberIdSchema, value: z.int().min(1) })),
  event('StreakShieldUsed', 1, z.object({ memberId: MemberIdSchema, week: z.string().min(1) })),
  event('StreakReset', 1, z.object({ memberId: MemberIdSchema, previous: z.int().min(0) })),
  /** Promotion is two steps on purpose: announced first, applied later. */
  event(
    'PromotionScheduled',
    1,
    z.object({
      taskId: TaskIdSchema,
      memberId: MemberIdSchema,
      fromRung: RungSchema,
      toRung: RungSchema,
      effectiveFrom: DayIdSchema,
    }),
  ),
  event(
    'RungChanged',
    1,
    z.object({
      taskId: TaskIdSchema,
      memberId: MemberIdSchema,
      fromRung: RungSchema,
      toRung: RungSchema,
      /** Always points at the announcement. A rung never changes unannounced. */
      scheduledBy: EventIdSchema,
    }),
  ),
  event('BadgeAwarded', 1, z.object({ memberId: MemberIdSchema, badgeId: BadgeIdSchema })),
  event(
    'TitleAwarded',
    1,
    z.object({ memberId: MemberIdSchema, title: z.string().trim().min(1).max(40) }),
  ),

  // -- Juegos ----------------------------------------------------------------
  /**
   * Una sesión de repaso terminada. No da monedas ni experiencia por sí misma:
   * si el repaso cuenta como misión del día, lo que paga es la validación de esa
   * misión, con las monedas que el padre le haya puesto. Así jugar más no da más,
   * y el juego no se puede minar.
   */
  event(
    'PracticeFinished',
    1,
    z.object({
      memberId: MemberIdSchema,
      game: GameIdSchema,
      /**
       * Sin esto, pulsar dos veces «terminar» guardaba la sesión dos veces y
       * doblaba su peso en lo que el juego decide practicar. Lo encontró una
       * prueba: la comprobación de duplicados existía en el mando y no servía
       * para nada, porque el evento no traía el identificador que se comprueba.
       */
      commandId: CommandIdSchema,
      items: z.array(PracticeItemSchema).min(1).max(40),
    }),
  ),

  // -- Parent access ---------------------------------------------------------
  event('PinChanged', 1, z.object({})),
  event('PinAttemptFailed', 1, z.object({ consecutiveFailures: z.int().min(1) })),
  event('PinLocked', 1, z.object({ until: InstantSchema, consecutiveFailures: z.int().min(1) })),
  event('ParentSessionOpened', 1, z.object({ deviceId: DeviceIdSchema })),

  // -- Meta ------------------------------------------------------------------
  event('DataExported', 1, z.object({ eventCount: z.int().min(0), photoCount: z.int().min(0) })),
  event('DataImported', 1, z.object({ eventCount: z.int().min(0), fromDeviceId: DeviceIdSchema })),
  /** The device clock went backwards or jumped. The day is flagged, not rejected. */
  event(
    'ClockAnomalyDetected',
    1,
    z.object({
      expectedAfter: InstantSchema,
      observed: InstantSchema,
      deltaMs: z.int(),
    }),
  ),
] as const

export const DomainEventSchema = z.discriminatedUnion('type', EVENT_SCHEMAS)

/** An event as the domain produces it, before the store stamps it. */
export type DomainEvent = z.infer<typeof DomainEventSchema>
export type DomainEventType = DomainEvent['type']

/** Narrow a domain event to one type, e.g. `EventOf<'TaskValidated'>`. */
export type EventOf<T extends DomainEventType> = Extract<DomainEvent, { type: T }>
export type PayloadOf<T extends DomainEventType> = EventOf<T>['payload']

// --- The stamp the store adds ------------------------------------------------

export const EventStampSchema = z.object({
  id: EventIdSchema,
  deviceId: DeviceIdSchema,
  /** Monotonic per device. Gaps mean loss or tampering. */
  seq: z.int().min(1),
  /** Hash of the previous event on this device; `GENESIS` for the first. */
  prevHash: z.string().min(1),
})
export type EventStamp = z.infer<typeof EventStampSchema>

export const GENESIS_HASH = 'genesis'

export const StoredEventSchema = z.intersection(EventStampSchema, DomainEventSchema)
export type StoredEvent = DomainEvent & EventStamp

export function isEventOf<T extends DomainEventType>(
  event: DomainEvent,
  type: T,
): event is EventOf<T> {
  return event.type === type
}

// --- Declared effects: the "never subtract" guard ----------------------------

/**
 * `xp` has no `decrease`, so the compiler itself rejects an event that would
 * lower experience. Coins may only decrease through spending, and a test asserts
 * that `PurchaseMade` is the only event allowed to.
 */
export interface EventEffect {
  readonly coins: 'increase' | 'decrease' | 'none'
  readonly xp: 'increase' | 'none'
  /** Who is allowed to emit it. Children can play; they cannot govern. */
  readonly allowedActors: readonly Actor['kind'][]
}

const CHILD_OR_PARENT: readonly Actor['kind'][] = ['child', 'parent']
const PARENT_ONLY: readonly Actor['kind'][] = ['parent']
const PARENT_OR_SYSTEM: readonly Actor['kind'][] = ['parent', 'system']
const SYSTEM_ONLY: readonly Actor['kind'][] = ['system']
const ANY_ACTOR: readonly Actor['kind'][] = ['child', 'parent', 'system']

const none = { coins: 'none', xp: 'none' } as const

export const EVENT_EFFECTS: Readonly<Record<DomainEventType, EventEffect>> = {
  FamilyConfigured: { ...none, allowedActors: PARENT_ONLY },
  MemberAdded: { ...none, allowedActors: PARENT_ONLY },
  MemberAdaptationsUpdated: { ...none, allowedActors: PARENT_ONLY },
  MemberArchived: { ...none, allowedActors: PARENT_ONLY },
  MemberRestored: { ...none, allowedActors: PARENT_ONLY },
  RewardMembersChanged: { ...none, allowedActors: PARENT_ONLY },
  TaskDefined: { ...none, allowedActors: PARENT_ONLY },
  TaskRenamed: { ...none, allowedActors: PARENT_ONLY },
  TaskRestored: { ...none, allowedActors: PARENT_ONLY },
  TaskStepsUpdated: { ...none, allowedActors: PARENT_ONLY },
  TaskRewardsChanged: { ...none, allowedActors: PARENT_ONLY },
  TaskReferencePhotoSet: { ...none, allowedActors: PARENT_ONLY },
  MemberLookChanged: { ...none, allowedActors: PARENT_ONLY },
  RemindersSet: { ...none, allowedActors: PARENT_ONLY },
  TaskArchived: { ...none, allowedActors: PARENT_ONLY },
  RewardDefined: { ...none, allowedActors: PARENT_ONLY },
  RewardArchived: { ...none, allowedActors: PARENT_ONLY },
  PriceChangeScheduled: { ...none, allowedActors: PARENT_ONLY },
  LimitChangeScheduled: { ...none, allowedActors: PARENT_ONLY },
  DayTypeDeclared: { ...none, allowedActors: PARENT_ONLY },
  DayRangeDeclared: { ...none, allowedActors: PARENT_ONLY },
  DayPaused: { ...none, allowedActors: PARENT_ONLY },
  PlanChangeAnnounced: { ...none, allowedActors: PARENT_ONLY },

  StepCompleted: { ...none, allowedActors: CHILD_OR_PARENT },
  StepUncompleted: { ...none, allowedActors: CHILD_OR_PARENT },
  TaskMarkedDone: { ...none, allowedActors: CHILD_OR_PARENT },
  TaskValidated: { coins: 'increase', xp: 'increase', allowedActors: CHILD_OR_PARENT },
  TaskReturned: { ...none, allowedActors: PARENT_ONLY },
  // A parent undoing their own mistake. It claws back at most what that one
  // validation gave, never more, and never touches experience.
  ValidationRevoked: { coins: 'decrease', xp: 'none', allowedActors: PARENT_ONLY },
  CheckRequested: { ...none, allowedActors: SYSTEM_ONLY },
  CheckResolved: { ...none, allowedActors: PARENT_ONLY },

  ChestOpened: { ...none, allowedActors: ANY_ACTOR },
  PurchaseMade: { coins: 'decrease', xp: 'none', allowedActors: CHILD_OR_PARENT },
  // Gives back exactly what that purchase took, never more. A child undoing
  // their own change of mind does not need an adult — and the system undoes it
  // too, when the day those minutes belonged to has gone by without an adult
  // ever granting them. A child does not lose coins because a grown-up forgot.
  PurchaseCancelled: { coins: 'increase', xp: 'none', allowedActors: ANY_ACTOR },
  PurchaseGranted: { ...none, allowedActors: PARENT_ONLY },
  PurchaseDelivered: { ...none, allowedActors: PARENT_ONLY },
  TimerStarted: { ...none, allowedActors: CHILD_OR_PARENT },

  StreakAdvanced: { ...none, allowedActors: SYSTEM_ONLY },
  StreakShieldUsed: { ...none, allowedActors: SYSTEM_ONLY },
  StreakReset: { ...none, allowedActors: SYSTEM_ONLY },
  PromotionScheduled: { ...none, allowedActors: PARENT_OR_SYSTEM },
  RungChanged: { ...none, allowedActors: PARENT_OR_SYSTEM },
  BadgeAwarded: { coins: 'none', xp: 'increase', allowedActors: SYSTEM_ONLY },
  TitleAwarded: { ...none, allowedActors: SYSTEM_ONLY },

  // Jugar no paga: paga la misión, si el padre ha puesto una.
  PracticeFinished: { ...none, allowedActors: CHILD_OR_PARENT },

  PinChanged: { ...none, allowedActors: PARENT_ONLY },
  PinAttemptFailed: { ...none, allowedActors: ANY_ACTOR },
  PinLocked: { ...none, allowedActors: SYSTEM_ONLY },
  ParentSessionOpened: { ...none, allowedActors: PARENT_ONLY },

  DataExported: { ...none, allowedActors: PARENT_ONLY },
  DataImported: { ...none, allowedActors: PARENT_ONLY },
  ClockAnomalyDetected: { ...none, allowedActors: SYSTEM_ONLY },
}

export const ALL_EVENT_TYPES = Object.keys(EVENT_EFFECTS) as readonly DomainEventType[]

/** Whether this actor is allowed to emit this kind of event at all. */
export function mayActorEmit(actor: Actor, type: DomainEventType): boolean {
  return EVENT_EFFECTS[type].allowedActors.includes(actor.kind)
}

export type { DeviceId, EventId, MemberId }
